/**
 * The hard-constraint vocabulary and state rule both constraint screens share
 * (PIVOT-232). No fetch and no JSX, so `lib/` (frontend/CLAUDE.md §2).
 *
 * `ConstraintsStep` asks these five questions during onboarding;
 * `ProfileReview` is where the same answers are corrected afterwards on
 * `/profile`. Two copies of the contract spellings would drift, and the
 * backend gate matches them exactly (`passes_contract` lowercases
 * `job.contract` and compares it against the stored list), so they live here
 * once.
 *
 * `SCHEDULE_KINDS` (PIVOT-299) is the matcher-v2 match-setup form's own
 * vocabulary, added here rather than duplicated in `MatchSetupForm.tsx`: it
 * is exactly the same kind of "backend-constrained string with a plain-word
 * label" as `CONTRACT_TYPES`, one file up from needing a second component to
 * justify sharing it.
 *
 * `LEVELS` (PIVOT-350) used to be two separate copies, one per form, and the
 * two forms disagreed on how "no answer yet" was spelled -- `''` in one,
 * `'unstated'` in the other -- while both meant the same silent default. One
 * copy here ends that drift the same way `CONTRACT_TYPES` already did.
 *
 * `PROVINCES` (PIVOT-352) is the checkbox grid `ConstraintsStep`,
 * `MatchSetupForm`, and `ProfileReview` each show in place of the old
 * free-text locations field. The `value` is the exact code
 * `pivot.ingestion.province.Province` returns and
 * `pivot.matching.filters.passes_location` reads back
 * (`pivot.ingestion.province.PROVINCE_CODES`) -- ticking a box stores that
 * code verbatim, never a translated label, so the gate matches regardless of
 * which locale the candidate answered in.
 */

import type { TranslationKey } from '../i18n/types'
import type { ConstraintState } from '../types/domain'

/**
 * The contract values the backend's gate reads, with their plain-word labels.
 *
 * The `value` is the stored string and is not translated — `passes_contract`
 * compares it to the posting's own `contract` field. Only the label changes
 * with the locale (constitution: no jargon in front of the user).
 */
export const CONTRACT_TYPES: { value: string; labelKey: TranslationKey }[] = [
  { value: 'cdi', labelKey: 'onboarding.constraintsContractCdi' },
  { value: 'cdd', labelKey: 'onboarding.constraintsContractCdd' },
  { value: 'freelance', labelKey: 'onboarding.constraintsContractFreelance' },
  { value: 'interim', labelKey: 'onboarding.constraintsContractInterim' },
]

/**
 * The level words `passes_language` reads, in the spelling it reads them in
 * (PIVOT-350). The first entry is a disabled placeholder (`''`) — never a
 * real choice, so a select left on it means "not yet answered", not "unstated"
 * (that word is gone from every form; the backend enum keeps it only so a row
 * saved before this ticket still loads). `'none'` is the candidate stating
 * they do NOT speak the language — a real, blocking answer, distinct from
 * leaving the question untouched.
 */
export const LEVELS: { value: string; labelKey: TranslationKey }[] = [
  { value: '', labelKey: 'onboarding.constraintsLevelChoose' },
  { value: 'none', labelKey: 'onboarding.constraintsLevelNone' },
  { value: 'basic', labelKey: 'onboarding.constraintsLevelBasic' },
  { value: 'intermediate', labelKey: 'onboarding.constraintsLevelIntermediate' },
  { value: 'fluent', labelKey: 'onboarding.constraintsLevelFluent' },
  { value: 'native', labelKey: 'onboarding.constraintsLevelNative' },
]

/**
 * The eleven Belgian provinces `resolve_province` (backend
 * `ingestion/province.py`) returns, region-grouped -- Brussels, then the five
 * Flemish provinces, then the five Walloon provinces -- so the wrapped grid
 * reads as three neighbourhoods rather than an alphabetised list a candidate
 * has to hunt through. German locale names are out of scope (no `de` locale
 * exists yet; frontend/src/types/domain.ts:9's `Locale` is `'fr' | 'nl' |
 * 'en'`), so each label is shown in whichever of those three the candidate
 * is using.
 */
export const PROVINCES: { value: string; labelKey: TranslationKey }[] = [
  { value: 'brussels', labelKey: 'onboarding.provinceBrussels' },
  { value: 'antwerpen', labelKey: 'onboarding.provinceAntwerp' },
  { value: 'limburg', labelKey: 'onboarding.provinceLimburg' },
  { value: 'oost-vlaanderen', labelKey: 'onboarding.provinceEastFlanders' },
  { value: 'west-vlaanderen', labelKey: 'onboarding.provinceWestFlanders' },
  { value: 'vlaams-brabant', labelKey: 'onboarding.provinceFlemishBrabant' },
  { value: 'hainaut', labelKey: 'onboarding.provinceHainaut' },
  { value: 'liege', labelKey: 'onboarding.provinceLiege' },
  { value: 'namur', labelKey: 'onboarding.provinceNamur' },
  { value: 'luxembourg', labelKey: 'onboarding.provinceLuxembourg' },
  { value: 'brabant-wallon', labelKey: 'onboarding.provinceWalloonBrabant' },
]

/** The stored codes in `PROVINCES`, for telling a province tick apart from a
 * legacy free-text location string (the removable-chip split every one of
 * `ConstraintsStep`/`MatchSetupForm`/`ProfileReview`'s location lists does).
 * A `Set`, not a re-typed list -- one definition of "is this a province
 * code" for the whole frontend, mirroring the backend's own
 * `PROVINCE_CODES` the same way. */
export const PROVINCE_CODE_SET: ReadonlySet<string> = new Set(PROVINCES.map((p) => p.value))

/**
 * Split a stored `locations` list into ticked province codes and legacy
 * free-text strings (PIVOT-352). A profile answered before this ticket
 * carries only the latter; a profile answered on the new grid carries only
 * the former; a profile mid-migration can carry both, and both kinds clear
 * a job independently (`passes_location` ORs the two checks).
 */
export function splitLocations(locations: string[]): {
  provinceCodes: string[]
  legacy: string[]
} {
  const provinceCodes: string[] = []
  const legacy: string[] = []
  for (const value of locations) {
    if (PROVINCE_CODE_SET.has(value)) {
      provinceCodes.push(value)
    } else {
      legacy.push(value)
    }
  }
  return { provinceCodes, legacy }
}

/** The schedule kinds `ScheduleValue` (backend `domain/policy_rules.py`)
 * accepts, with their plain-word labels. */
export const SCHEDULE_KINDS: { value: string; labelKey: TranslationKey }[] = [
  { value: 'day', labelKey: 'onboarding.matchSetupScheduleDay' },
  { value: 'evening', labelKey: 'onboarding.matchSetupScheduleEvening' },
  { value: 'night', labelKey: 'onboarding.matchSetupScheduleNight' },
  { value: 'weekend', labelKey: 'onboarding.matchSetupScheduleWeekend' },
  { value: 'rotating_shift', labelKey: 'onboarding.matchSetupScheduleRotatingShift' },
  { value: 'on_call', labelKey: 'onboarding.matchSetupScheduleOnCall' },
]

/** `PayValue.period` (backend `domain/policy_rules.py`), with plain-word
 * labels. Shared by `MatchSetupForm` (the picker) and `PolicyReview` (display
 * of what was picked), so the two screens never name a period differently. */
export const PAY_PERIODS: { value: string; labelKey: TranslationKey }[] = [
  { value: 'hour', labelKey: 'onboarding.matchSetupPayPeriodHour' },
  { value: 'day', labelKey: 'onboarding.matchSetupPayPeriodDay' },
  { value: 'month', labelKey: 'onboarding.matchSetupPayPeriodMonth' },
  { value: 'year', labelKey: 'onboarding.matchSetupPayPeriodYear' },
  { value: 'unknown', labelKey: 'onboarding.matchSetupPayPeriodUnknown' },
]

/** `PayValue.gross_or_net`, with plain-word labels. Same sharing reason as
 * `PAY_PERIODS`. */
export const PAY_GROSS_OR_NET: { value: string; labelKey: TranslationKey }[] = [
  { value: 'gross', labelKey: 'onboarding.matchSetupPayGross' },
  { value: 'net', labelKey: 'onboarding.matchSetupPayNet' },
  { value: 'unknown', labelKey: 'onboarding.matchSetupPayGrossOrNetUnknown' },
]

/** Looks up one vocabulary entry's label key by its stored value, falling
 * back to the value itself only if it is somehow not in the table (an older
 * rule stamped before a vocabulary entry was added/renamed) — never a raw
 * untranslated code when the table already knows the word. */
export function vocabLabel(
  table: { value: string; labelKey: TranslationKey }[],
  value: string,
  t: (key: TranslationKey) => string,
): string {
  const entry = table.find((item) => item.value === value)
  return entry ? t(entry.labelKey) : value
}

/**
 * What to record for one constraint, given the two controls that answer it.
 *
 * `previous` is what the profile already says, and it is returned untouched
 * when the candidate neither gave a value nor ticked the no-constraint box.
 * That is what keeps a profile written before this step at `never_asked` when
 * someone edits their summary on `/profile` and never looks at these fields:
 * a save must not invent "asked, and unconstrained" out of a question nobody
 * answered. There is deliberately no way back to `never_asked` from here —
 * once a candidate has answered, that fact stands (the server carries it the
 * same way; see `OnboardingService.apply_edits`).
 */
export function constraintState(
  hasValue: boolean,
  noConstraint: boolean,
  previous: ConstraintState,
): ConstraintState {
  if (hasValue) return 'answered'
  if (noConstraint) return 'no_constraint'
  return previous
}
